import { useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { signUpDataAtom, pushStepAtom, stepStackAtom } from "store/signUpStore";
import { useCheckDuplicateCtn } from "hooks/useCheckDuplicateCtn";
import { IdentityBirthGenderForm, IdentityBirthGenderResult } from "components/identity/IdentityBirthGenderForm";
import { useSignupPolicy } from "hooks/useSignupPolicy";

/**
 * Step2: 생년월일 + 성별
 *
 * stepStack 등장 횟수로 회차 판정:
 *   - 0회차: 본인 — birth/gender/parentConsent
 *   - 1회차: 14세 미만 보호자 — parentBirth/parentGender (0회차에서 바로 재진입)
 *
 * 14세 미만 흐름: Step2#1(미성년자 b/g) → Step2#2(보호자 b/g) → Step3(보호자 인증정보)
 *   → Step5(보호자 다날) → Step4(미성년자 휴대폰) → Step5(미성년자 SMS OTP)
 *
 * SNS 제공값(birth/gender)은 prefill 만 적용 — 사용자가 수정 가능.
 *   다날 인증은 통신사 가입 정보와 매칭하므로 SNS 의 self-declared 값이 불일치할 수 있음.
 */
export const SignUpStep2 = () => {
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);
    const stepStack = useAtomValue(stepStackAtom);
    const { navigateToAuth } = useCheckDuplicateCtn();
    const { check: checkPolicy, isChecking } = useSignupPolicy();
    // 판정 요청을 보낸 직후 같은 렌더 안에서 다시 눌러도 막는다 (상태는 다음 렌더에야 바뀐다)
    const submittingRef = useRef(false);
    if (!isChecking) submittingRef.current = false;

    // 현재 step(마지막) 제외, 이전 스택에 2 가 이미 있으면 보호자 회차
    const isParentRound = stepStack.slice(0, -1).includes(2);

    const handleNext = (result: IdentityBirthGenderResult) => {
        // 판정 중 다시 누르면 무시 (중복 요청·중복 이동 방지)
        if (isChecking || submittingRef.current) return;
        if (isParentRound) {
            // 보호자 생년월일·성별 → 보호자 인증정보 입력(Step3#2)
            setSignUpData(prev => ({
                ...prev,
                parentBirth: result.birth,
                parentGender: result.gender as "MALE" | "FEMALE" | "",
            }));
            pushStep(3);
            return;
        }

        // 본인(미성년자 / 14세 이상)
        const { birth, gender, parentConsent } = result;
        const updated = { ...signUpData, birth, gender, parentConsent };
        setSignUpData(updated);

        const { countryCd } = updated;

        // 기존 흐름: 14세 미만(KR) → 보호자 생년월일·성별 입력(Step2 재진입), 그 외 → 인증
        const legacyNext = () => {
            if (parentConsent && countryCd === "KR") {
                pushStep(2);
                return;
            }
            navigateToAuth(countryCd);
        };

        // 가입 연령 정책은 항상 서버에 생년월일로 판정을 받는다 (진입 시 조회가 늦거나 실패했어도 여기서 확정).
        //   정책 미적용 응답·조회 실패면 기존 흐름 그대로, 적용이면 가입 불가 / 보호자 동의 대상 / 가입 가능 분기.
        submittingRef.current = true;
        checkPolicy(
            { serviceId: updated.serviceId, birth, countryCd, ctnCountryCd: updated.ctnCountryCd || undefined },
            legacyNext,
        );
    };

    if (isParentRound) {
        return (
            // key 로 회차별 별도 인스턴스 — Step2#1→#2 전환 시 step 이 2 로 유지되어
            //   IdentityBirthGenderForm 이 재사용되면 미성년자 입력값이 보호자 화면에 잔존함.
            <IdentityBirthGenderForm
                key="identity-parent"
                initialBirth={signUpData.parentBirth}
                initialGender={signUpData.parentGender}
                enableParentConsent={false}
                // 보호자도 KR 다날 → genderDigit 필요 → OTHER 차단.
                enableOtherGender={false}
                titleI18nKey="signUp.step2.titleParent"
                infoI18nKey="signUp.step2.infoParent"
                onNext={handleNext}
            />
        );
    }

    return (
        <IdentityBirthGenderForm
            key="identity-self"
            initialBirth={signUpData.birth}
            initialGender={signUpData.gender}
            initialParentConsent={signUpData.parentConsent}
            // SNS 가 제공한 birth/gender 는 prefill 만 하고 잠그지 않는다.
            //   카카오·네이버 계정의 임의 입력값이라 다날(통신사 가입정보 대조)과 불일치할 수 있고,
            //   잠그면 인증 실패 시 사용자가 고칠 방법이 없어 가입이 막힌다.
            // 가입 연령 정책 적용 시에는 14세 미만 판정·보호자 절차를 서버가 정하므로 체크박스를 쓰지 않는다.
            enableParentConsent={!signUpData.policyEnabled}
            // KR 은 다음 단계가 다날 → genderDigit 필요 → OTHER 차단. 해외(SMS OTP)·본인인증 미사용은 허용.
            enableOtherGender={signUpData.countryCd !== "KR" || (signUpData.policyEnabled && !signUpData.idvEnabled)}
            onNext={handleNext}
        />
    );
};
